import React, { useState, useEffect, useRef } from "react";
import { useContactModal } from "../context/ContactModalContext";
import { submitToWeb3Forms } from "../utils/web3forms";

const products = ["Vegetables", "Spices", "Fresh Fruits", "Processed", "Other"];
const initialForm = {
  name: "", company: "", email: "", country: "",
  product: "Vegetables", volume: "", port: "", message: "",
};

export default function ContactModal() {
  const { isOpen, closeModal } = useContactModal();
  const modalRef = useRef(null);
  const startedAtRef = useRef(Date.now());
  const [form, setForm] = useState(initialForm);
  const [botcheck, setBotcheck] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeModal(); };
    if (isOpen) {
      startedAtRef.current = Date.now();
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      if (modalRef.current) {
        setTimeout(() => {
          modalRef.current.scrollTop = modalRef.current.scrollHeight;
        }, 0);
      }
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, closeModal]);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErrorMsg("");
    try {
      await submitToWeb3Forms({
        data: form,
        subject: `Export Inquiry — ${form.product} — ${form.name}`,
        from_name: form.name || "Website Inquiry",
        botcheck,
        startedAt: startedAtRef.current,
      });
      setStatus("success");
      setTimeout(() => {
        setStatus("idle");
        setForm(initialForm);
        closeModal();
      }, 2500);
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-md"
        onClick={closeModal}
      />

      {/* Panel */}
      <div ref={modalRef} className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden rounded-3xl bg-[#F5F1E8]/97 border border-stone-900/10 shadow-2xl shadow-black/20 fade-up">

        {/* Glow accents - contained */}
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-indigo-500/15 blur-3xl pointer-events-none overflow-hidden" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-amber-400/10 blur-3xl pointer-events-none overflow-hidden" />

        {/* Header */}
        <div className="relative flex items-start justify-between p-5 sm:p-7 pb-0">
          <div>
            <div className="inline-flex items-center gap-2 glass-pill rounded-full px-3 py-1 mb-3">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-300" />
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-amber-700">Export Inquiry</span>
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-semibold leading-tight text-stone-900">
              Tell us what you need.
            </h2>
            <p className="mt-1.5 text-sm text-stone-500">
              Our export team replies within one business day.
            </p>
          </div>
          <button
            onClick={closeModal}
            aria-label="Close"
            className="shrink-0 w-9 h-9 rounded-xl glass hover:bg-stone-900/6 transition flex items-center justify-center ml-4 mt-1 cursor-pointer"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="relative p-5 sm:p-7 pt-6 space-y-4">
          {/* Honeypot — hidden from users, bots tend to fill it */}
          <input
            type="checkbox"
            name="botcheck"
            tabIndex="-1"
            autoComplete="off"
            checked={botcheck}
            onChange={(e) => setBotcheck(e.target.checked)}
            style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0 }}
            aria-hidden="true"
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your Name"  name="name"    value={form.name}    onChange={handle} required />
            <Field label="Company"    name="company" value={form.company} onChange={handle} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email" type="email" name="email" value={form.email} onChange={handle} required />
            <Field label="Country"    name="country" value={form.country} onChange={handle} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Volume (MT / container)" name="volume" value={form.volume} onChange={handle} placeholder="e.g. 1 × 40′ reefer" />
            <Field label="Destination Port"        name="port"   value={form.port}   onChange={handle} placeholder="e.g. Rotterdam" />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-stone-400 block mb-2.5">
              Product Interest
            </label>
            <div className="flex flex-wrap gap-2">
              {products.map(p => (
                <button key={p} type="button" onClick={() => setForm({ ...form, product: p })}
                  className={`px-4 py-2 rounded-xl text-sm transition cursor-pointer ${form.product === p
                    ? "bg-gradient-to-br from-amber-200 to-amber-500 text-amber-950 font-medium shadow-lg"
                    : "glass-pill text-stone-600 hover:bg-stone-900/8"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-stone-400 block mb-2">Message</label>
            <textarea
              name="message" rows={4} value={form.message} onChange={handle}
              placeholder="Packaging needs, timelines, certifications required..."
              className="w-full bg-stone-900/5 border border-stone-900/10 focus:border-amber-500/50 rounded-2xl px-4 py-3 text-sm text-stone-900 placeholder-stone-400 outline-none transition resize-none"
            />
          </div>

          {status === "error" && (
            <p className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              {errorMsg}
            </p>
          )}

          <div className="flex items-center justify-between gap-4 pt-1">
            <p className="text-[11px] text-stone-400">
              By submitting you agree to our Privacy Policy.
            </p>
            <button
              type="submit"
              disabled={status === "sending" || status === "success"}
              className="shrink-0 inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl text-sm font-medium text-white bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-700 hover:from-emerald-600 hover:to-emerald-700 transition shadow-xl shadow-emerald-600/25 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {status === "success" ? (
                <>
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Sent — we'll be in touch
                </>
              ) : status === "sending" ? (
                <>
                  <svg viewBox="0 0 24 24" className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v4m0 8v4m8-8h-4M8 12H4m13.66-5.66l-2.83 2.83M9.17 14.83l-2.83 2.83m0-11.32l2.83 2.83m5.66 5.66l2.83 2.83" />
                  </svg>
                  Sending…
                </>
              ) : (
                <>
                  Send Inquiry
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-stone-400 block mb-2">
        {label}{required && <span className="text-amber-600"> *</span>}
      </label>
      <input
        type={type} name={name} value={value} onChange={onChange}
        required={required} placeholder={placeholder}
        className="w-full bg-stone-900/5 border border-stone-900/10 focus:border-amber-500/50 rounded-2xl px-4 py-3 text-sm text-stone-900 placeholder-stone-400 outline-none transition"
      />
    </div>
  );
}
